import { Dialog } from "../ui/Dialog"
import { useTranslation } from "react-i18next"
import { useMemo } from "react"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { useUserQuery } from "@/api/users-api"
import logoUrl from "@assets/logo.png?url"
import { UserProfile } from "../ui/UserProfile"
import { CrossOriginImage } from "../ui/CrossOriginImage"
import { StyleCover } from "../Styles/StyleCover"
import { ImageFormat, processImageUrl } from "@/utils/process-image-url"
import { Button } from "../ui/Button"
import { StyleInfoRecord } from "@/lib/catalog"

export function StyleInfoDialog(props: { style: StyleInfoRecord }) {
	const { t } = useTranslation("components")
	const { style } = props
	const userQuery = useUserQuery()
	const user = userQuery.data

	const title = style.name
	const description = style?.description
	const coverPhoto = style?.coverImageUrl
	const author =
		style?.author.name === "Pixieset" && !isPixiesetAuthEnabled()
			? "Polarr"
			: style?.author.name

	const authorProfile = useMemo(() => {
		if (style.author.profileURL) {
			return (
				<CrossOriginImage
					className="flex size-7 items-center justify-center rounded-full border border-base-600 dark:border-transparent"
					src={style.author.profileURL}
				/>
			)
		} else {
			if (style.visibility === "public") {
				return (
					<div className="flex size-7 items-center justify-center rounded-full">
						<img className="size-4" src={logoUrl} alt={t("styleInfoDialog.altLogo")} />
					</div>
				)
			} else if (user) {
				return <UserProfile size={"xsmall"} user={user} />
			}
		}
	}, [user, style])

	const styleDescription = useMemo(() => {
		if (!style) {
			return null
		}

		if (style?.visibility === "public") {
			return (
				<p className="text-sm font-normal text-base-100" style={{ lineHeight: "23px" }}>
					{description}
				</p>
			)
		} else {
			return (
				<div
					className="flex flex-col text-sm font-normal text-base-100"
					style={{ lineHeight: "23px" }}
				>
					<p>
						{t("styleInfoDialog.usingReferenceImages", {
							var0: style.referenceCount ?? 0,
						})}
					</p>
					<p>
						{t("styleInfoDialog.createdOn", {
							var0: new Date(style.createdAt ?? new Date()).toLocaleDateString(
								"en-US",
								{
									month: "short",
									day: "numeric",
									year: "numeric",
								},
							),
						})}
					</p>
				</div>
			)
		}
	}, [description, style])

	return (
		<Dialog
			contentSize="md-h-auto"
			canGoBack={false}
			contentProps={{ className: "min-w-[508px]" }}
			trackingViewName="style_info_dialog"
		>
			<div className="flex size-full flex-col space-y-4 pb-4 pt-2">
				<div className="flex w-full flex-row space-x-4">
					<div className="flex flex-1 flex-col space-y-6">
						<div className="space-y-4">
							<h2 className="text-xl font-bold transition-all duration-300 ease-in-out">
								{title}
							</h2>
							<div className="flex items-center space-x-6 text-sm text-black dark:text-white">
								<div className="flex items-center space-x-2">
									{authorProfile}
									<span>{author}</span>
								</div>
							</div>
						</div>

						{styleDescription}

						<div className="flex w-56 flex-row items-center space-x-2">
							<Button
								disabled={true}
								trackingValue={title}
								trackingLabel="style_in_use"
								aria-label={t("styleInfoDialog.ariaAIStyleAlreadyInUse")}
								variant="default"
								className="h-8 flex-1 transition-all duration-300"
							>
								{t("styleInfoDialog.inUse")}
							</Button>
						</div>
					</div>
					<div className="w-full min-w-56 md:w-1/2">
						<StyleCover
							url={
								coverPhoto
									? processImageUrl({
											url: coverPhoto,
											format: ImageFormat.AVIF,
										})
									: undefined
							}
							title={title}
						/>
					</div>
				</div>
			</div>
		</Dialog>
	)
}
